import React from 'react';
import CreateActions, { type CreateAction } from './CreateActions';
import { Icon } from './Icon';
import type { IconName } from './Icon';

/**
 * A header action. The PRIMARY ones (no `color`, `primary !== false`) are
 * rendered through {@link CreateActions}, so they collapse to a round button
 * or a `+` menu on mobile — and so they can finally carry `id` (tour anchors)
 * and `requires` (role gate), neither of which the old raw <button> supported.
 *
 * The `color` variants stay as bespoke buttons: they are material-identity
 * skins (filament / resin / supply), not creates.
 */
export interface HeaderAction extends Partial<Pick<CreateAction, 'requires' | 'disabledReason' | 'id'>> {
    label: string;
    icon: IconName;
    onClick: () => void;
    primary?: boolean;
    color?: 'primary' | 'amber' | 'slate' | 'filament' | 'resin' | 'supply';
    /** Greys out the button and blocks the click (e.g. nothing to save / busy). */
    disabled?: boolean;
}

export interface PageHeaderProps {
    title: string;
    subtitle?: React.ReactNode;
    icon: IconName;
    actions?: HeaderAction[];
    children?: React.ReactNode;
    /**
     * Controls that belong on the TITLE row, at its right on desktop and under
     * it on a phone: what the whole page is looking at (Financiero's period).
     * Not for creates — those are `actions`.
     */
    aside?: React.ReactNode;
}

const PageHeader: React.FC<PageHeaderProps> = ({ title, subtitle, icon, actions, children, aside }) => {
    // Primary creates go through CreateActions (mobile-aware); the coloured
    // material skins keep their bespoke button.
    const primaryActions: CreateAction[] = (actions ?? [])
        .filter((a) => !a.color && a.primary !== false)
        .map((a) => ({
            label: a.label, icon: a.icon, onClick: a.onClick,
            requires: a.requires, disabled: a.disabled,
            disabledReason: a.disabledReason, id: a.id,
        }));
    const colouredActions = (actions ?? []).filter((a) => a.color || a.primary === false);
    const hasButtons = (actions && actions.length > 0) || !!children;

    return (
        <header className="flex flex-col gap-3 mb-8">
            {/* Title row */}
            <div className={aside ? 'flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between' : undefined}>
                <div className="flex flex-col">
                    <div className="flex items-center gap-3 mb-1">
                        <Icon name={icon} className="text-primary !text-3xl shrink-0" aria-hidden />
                        <h1 className="text-3xl font-medium !text-slate-600 dark:!text-white tracking-tight leading-tight">
                            {title}
                        </h1>
                    </div>
                    {subtitle && (
                        <p className="text-text-sub dark:text-slate-400 ml-1 font-medium">
                            {subtitle}
                        </p>
                    )}
                </div>
                {aside}
            </div>

            {/* Buttons row — always below title, left-aligned */}
            {hasButtons && (
                <div className="flex flex-wrap gap-3">
                    {children}
                    {primaryActions.length > 0 && (
                        <CreateActions size="lg" actions={primaryActions} />
                    )}
                    {colouredActions.map((action, index) => {
                        const color = action.color;
                        const isPrimary = action.primary !== false && !color;

                        let buttonClass = '';
                        if (color === 'filament') {
                            buttonClass = 'bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400 border border-amber-100 dark:border-amber-800/30 hover:bg-amber-100 dark:hover:bg-amber-900/30 shadow-sm';
                        } else if (color === 'resin') {
                            buttonClass = 'bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-400 border border-purple-100 dark:border-purple-800/30 hover:bg-purple-100 dark:hover:bg-purple-900/30 shadow-sm';
                        } else if (color === 'supply') {
                            buttonClass = 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-slate-700 hover:bg-slate-200 dark:hover:bg-slate-700 shadow-sm';
                        } else if (color === 'amber') {
                            buttonClass = 'bg-amber-500 hover:bg-amber-600 text-white shadow-lg shadow-amber-500/10';
                        } else if (color === 'slate') {
                            buttonClass = 'bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700';
                        } else if (isPrimary) {
                            buttonClass = 'bg-primary hover:bg-primary-dark text-white shadow-lg shadow-primary/20';
                        } else {
                            buttonClass = 'bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700';
                        }

                        return (
                            <button
                                key={index}
                                onClick={action.onClick}
                                disabled={action.disabled}
                                className={`h-14 px-4 rounded-xl font-bold text-sm flex items-center gap-2 transition-all hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 disabled:active:scale-100 ${buttonClass}`}
                            >
                                <Icon name={action.icon} className="!text-[18px]" />
                                {action.label}
                            </button>
                        );
                    })}
                </div>
            )}
        </header>
    );
};

export default PageHeader;
